import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDateTimeShort } from "@/lib/dates";
import { Heart, MapPin, Send, FileText, AlertTriangle, MessageSquare } from "lucide-react";

export default function FamilyPortal() {
  const [data, setData] = useState(null);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [routing, setRouting] = useState("general");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("home");
  const messagesEnd = useRef(null);

  const loadHome = () => api.get("/family/me").then(({ data }) => setData(data));
  const loadThreads = () => api.get("/family/threads").then(({ data }) => {
    setThreads(data.threads || []);
    if (!active && data.threads?.length) setActive(data.threads[0].service_user_id);
  });

  useEffect(() => { loadHome(); loadThreads(); }, []);
  useEffect(() => { if (messagesEnd.current) messagesEnd.current.scrollIntoView({ behavior: "smooth" }); }, [threads, active]);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (!data.service_user) return <div className="bg-white border border-slate-200 rounded-lg p-6 text-slate-700">No linked service user yet. Please contact the care team.</div>;

  const su = data.service_user;
  const activeThread = threads.find((t) => t.service_user_id === active);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/family/messages", { service_user_id: active || su.id, message: msg, routing });
      setMsg("");
      await loadThreads();
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-5">
      <header className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 flex items-center gap-4 cc-glow">
        <div className="h-14 w-14 rounded-full bg-[#E0F2F2] flex items-center justify-center">
          <Heart className="h-6 w-6 text-[#0D7377]" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-[#0D7377]">Family portal</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{su.preferred_name || su.full_name}</h1>
          <p className="text-sm text-slate-600">{(su.service_types || []).join(", ")} · {su.address?.city}</p>
        </div>
      </header>

      <nav className="border-b border-slate-200 flex gap-1" role="tablist">
        {[["home", "Care record"], ["messages", "Messages"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`family-tab-${k}`} className={`px-4 py-2 text-sm font-medium ${tab === k ? "text-[#0D7377] border-b-2 border-[#0D7377]" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
        ))}
      </nav>

      {tab === "home" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><MapPin className="h-4 w-4" /> Upcoming visits</h3>
            <ul className="divide-y divide-slate-100">
              {data.upcoming_visits.map((v) => (
                <li key={v.id} className="py-2 flex items-center justify-between">
                  <p className="text-sm text-slate-800">{fmtDateTimeShort(v.scheduled_start)}</p>
                  <StatusBadge status="scheduled" />
                </li>
              ))}
              {data.upcoming_visits.length === 0 && <li className="py-2 text-sm text-slate-500">None planned in the coming week.</li>}
            </ul>
          </section>
          <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Recent visits</h3>
            <ul className="divide-y divide-slate-100">
              {data.recent_visits.slice(0, 6).map((v) => (
                <li key={v.id} className="py-2">
                  <p className="text-sm font-medium text-slate-900">{fmtDateTimeShort(v.checkout_time || v.scheduled_start)}</p>
                  <p className="text-xs text-slate-500">{v.actual_duration_mins} min · {v.visit_notes?.mood || "—"}</p>
                </li>
              ))}
            </ul>
          </section>
          <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 lg:col-span-2">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><FileText className="h-4 w-4" /> Care plan</h3>
            {data.care_plan ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(data.care_plan.sections || {}).map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{v}</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">Care plan not yet shared.</p>}
          </section>
          <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Daily notes</h3>
            <ul className="divide-y divide-slate-100 max-h-80 overflow-auto">
              {data.daily_notes.slice(0, 20).map((n) => (
                <li key={n.id} className="py-2">
                  <p className="text-xs text-slate-500">{fmtDateTimeShort(n.created_at)}</p>
                  <p className="text-sm text-slate-800">{n.note_text}</p>
                </li>
              ))}
              {data.daily_notes.length === 0 && <p className="py-2 text-sm text-slate-500">No notes yet.</p>}
            </ul>
          </section>
        </div>
      )}

      {tab === "messages" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="family-messages">
          <aside className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 space-y-1">
            {threads.map((t) => (
              <button key={t.service_user_id} onClick={() => setActive(t.service_user_id)} data-testid={`thread-tab-${t.service_user_id}`}
                className={`w-full text-left px-3 py-2 rounded ${active === t.service_user_id ? "bg-[#E0F2F2] text-[#0D7377]" : "hover:bg-slate-50"}`}>
                <p className="text-sm font-medium">{t.service_user_name}</p>
                <p className="text-xs text-slate-500">{t.messages.length} message{t.messages.length === 1 ? "" : "s"}</p>
                {t.unanswered_age_hours != null && t.unanswered_age_hours > 48 && (
                  <p className="text-[10px] text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Awaiting reply {Math.round(t.unanswered_age_hours)}h</p>
                )}
              </button>
            ))}
          </aside>
          <div className="md:col-span-2 bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col h-[60vh]">
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="message-list">
              {(activeThread?.messages || []).map((m) => (
                <div key={m.id} className={`max-w-md ${m.from_user_role === "family_member" ? "ml-auto bg-[#0D7377] text-white" : "bg-slate-100 text-slate-800"} rounded-lg px-3 py-2`}>
                  {m.auto_flagged && <p className="text-[10px] font-semibold inline-flex items-center gap-1 mb-1"><AlertTriangle className="h-3 w-3" /> Flagged: {(m.flagged_keywords || []).join(", ")}</p>}
                  <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                  <p className={`text-[10px] mt-1 ${m.from_user_role === "family_member" ? "text-white/80" : "text-slate-500"}`}>{m.from_name} · {fmtDateTimeShort(m.created_at)}</p>
                  {(m.replies || []).map((r) => (
                    <div key={r.id} className="mt-2 pt-2 border-t border-white/30 text-sm">
                      <p className="text-[10px] font-semibold opacity-80">↪ {r.from_name}</p>
                      <p>{r.message}</p>
                    </div>
                  ))}
                </div>
              ))}
              <div ref={messagesEnd} />
              {!activeThread?.messages?.length && <p className="text-sm text-slate-500 text-center py-8">No messages yet — start the conversation.</p>}
            </div>
            <form onSubmit={send} className="border-t border-slate-200 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Send as:</span>
                <label className="inline-flex items-center gap-1"><input type="radio" name="route" checked={routing === "general"} onChange={() => setRouting("general")} data-testid="routing-general" /> General message</label>
                <label className="inline-flex items-center gap-1"><input type="radio" name="route" checked={routing === "concern"} onChange={() => setRouting("concern")} data-testid="routing-concern" /> Raise a concern (mgmt only)</label>
              </div>
              <div className="flex gap-2">
                <textarea required value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Write your message…" className="flex-1 px-3 py-2 border border-slate-300 rounded-md" data-testid="family-message-input" />
                <button disabled={sending} data-testid="family-message-send" className="bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium inline-flex items-center gap-2 self-end">
                  <Send className="h-4 w-4" /> Send
                </button>
              </div>
              <p className="text-[10px] text-slate-500"><MessageSquare className="h-3 w-3 inline" /> Messages mentioning concerns (e.g. "hurt", "complaint") are automatically routed to management.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
